/*
 * ===============================================================================================================
 *                                Copyright 2020, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LuiModal } from '@jda/lui-common-component-library-mui5';
import { Button, TextField, Box, RadioGroup, Radio, FormControlLabel, FormLabel, FormControl } from '@mui/material';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import { useStyles } from './styles';
import Loader from '../../shared/Loader';
import { createNewClient } from '../../services/client';
import interpolateString from '../../utility/stringInterpolation';
import { selectRealmInfo } from '../../store/realmSettings/realmSettingsSelector';
import {
  MACHINE_CREATE_SUCCESS,
  MACHINE_CREATE_ERROR,
  MACHINE_NAME_IS_REQUIRED,
  MACHINE_TYPE,
  MACHINE_TYPE_IS_REQUIRED,
  // CLASSIC,
  REALM_TYPE,
  // MIGRATION_STATUS,
  CLOSE,
  ADD_MACHINE,
  MACHINE_NAME,
  MACHINE_CATEGORY
} from './contants';
import { setCurrentMachineName, setCurrentMachineSelection } from '../../store/machine/machineActionCreator';

// const allowedRealmStatusForEFS = [MIGRATION_STATUS.PREPARED, MIGRATION_STATUS.DUAL, MIGRATION_STATUS.MODERN ];
const machineTypes = [
  { key: '1', displayName: 'API', value: 'apim' },
  { key: '2', displayName: 'Event', value: 'EFS' }
  // { key: '3', displayName: 'SnowFlake', value: 'snowflake' },
];
interface IAddMachineDialogProps {
  openModal: boolean;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
  refreshOnSave: Function;
  displaySnackbar: Function;
  hideSnackbar: Function;
}

export const isSecretAndClientIdAvailable = (data: any) =>
  data.passwordCredentials[0].secretText && data.appId ? true : false;

const AddMachineDialog = ({
  openModal,
  openCloseModal,
  displaySnackbar,
  hideSnackbar,
  refreshOnSave
}: IAddMachineDialogProps) => {
  interface FormValues {
    displayName: string;
    type?: string;
    isExternal?: boolean; // Make isExternal optional
  }

  const formDefaultValues: FormValues = {
    displayName: '',
    type: 'apim', // Default value for machine type
    isExternal: false // Default value for machine category
  };

  const formErrorDefaultValues = {
    machineName: { error: false, errorHelperText: '' },
    type: { error: false, errorHelperText: '' }
  };
  const dispatch = useDispatch();
  const selectedRealmInfo = useSelector(selectRealmInfo);

  const [showCopyBtn, showHideCopyBtn] = useState(false);
  const [formValues, updateFormValues] = useState(formDefaultValues);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [showLoader, showHideLoader] = useState(false);
  const efsFFEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.efsMachineFF);
  const { classes } = useStyles();
  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const updateFormFields = (e: { target: { value: string } }) => {
    updateFormError(formErrorDefaultValues);
    updateFormValues({ ...formValues, displayName: e.target.value });
  };

  const validateRequired = (values: any) => {
    if ((selectedRealmInfo?.envType === 'admin' || efsFFEnabled) && values.displayName !== '' && values.type !== '') return true;
    else if (!efsFFEnabled && values.displayName !== '') return true;
    else return false;
  };
  // if it is application realm then isExternal will always be true else false for it
  const handleSubmit = () => {
    if (validateRequired(formValues)) {
      const formValuesCopy = { ...formValues };
      if (selectedRealmInfo?.envType !== 'admin' || formValues?.type !== 'apim') {
        delete formValuesCopy.isExternal;
      }
      updateFormValues(formValuesCopy);
      showHideLoader(true);
      hideSnackbar();
      showHideCopyBtn(false);
      showHideLoader(true);
      createNewClient(formValuesCopy)
        .then(response => {
          showHideLoader(false);
          displaySnackbar(interpolateString([formValues.displayName], MACHINE_CREATE_SUCCESS), 'success');
          openCloseModal(!openModal);
          dispatch(setCurrentMachineSelection(response));
          dispatch(setCurrentMachineName(response.displayName));
          refreshOnSave(response.id, response?.secrets?.[0]?.secretText);
          showHideLoader(false);
        })
        .catch(() => {
          showHideLoader(false);
          displaySnackbar(MACHINE_CREATE_ERROR, 'error');
          openCloseModal(!openModal);
        });
    } else {
      if (formValues.displayName === '')
        updateFormError({ ...formErrorState, machineName: { error: true, errorHelperText: MACHINE_NAME_IS_REQUIRED } });
      if (formValues.displayName !== '' && formValues.type === '')
        updateFormError({ ...formErrorState, type: { error: true, errorHelperText: MACHINE_TYPE_IS_REQUIRED } });
    }
  };

  const handleChangeMachineCategory = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ ...formValues, isExternal: event.target.value === 'External' });
  };
  const handleMachineTypeChange = (event: any) => {
    const selectedType = machineTypes.find(type => type.value === event.target.value);
    if (selectedType) {
      updateFormValues({ ...formValues, type: selectedType.value });
      updateFormError(formErrorDefaultValues);
    } else {
      updateFormValues({ ...formValues, type: '' });
      updateFormError({ ...formErrorState, type: { error: true, errorHelperText: MACHINE_TYPE_IS_REQUIRED } });
    }
  };
  return (
    <LuiModal
      data-testid="add-machine-dialog"
      isOpen={openModal}
      modalTitle="Add Machine"
      handleClose={handleClose}
      secondaryAction={
        <Button
          variant="contained"
          name="cancelBtn"
          color="primary"
          disableElevation
          onClick={handleClose}
          className={!showCopyBtn ? classes.hide : ''}
        >
          {CLOSE}
        </Button>
      }
      primaryAction={
        <Button
          variant="contained"
          name="createMachineBtn"
          color="primary"
          disableElevation
          disabled={showLoader || showCopyBtn}
          onClick={handleSubmit}
          className={showCopyBtn ? classes.hide : ''}
        >
          {ADD_MACHINE}
        </Button>
      }
      modalSize={'xs'}
    >
      <Box className={classes.modalFormContent}>
        {!showLoader && !showCopyBtn && (
          <TextField
            disabled={false}
            autoFocus
            fullWidth
            id="machineName"
            name="displayName"
            label={MACHINE_NAME}
            variant="standard"
            className={classes.modalTextBox}
            defaultValue={formValues.displayName}
            onChange={updateFormFields}
            error={formErrorState.machineName.error}
            helperText={formErrorState.machineName.errorHelperText}
            inputProps={{ maxLength: 100 }}
            required={true}
          />
        )}
      </Box>
      {(selectedRealmInfo?.envType === 'admin' || efsFFEnabled) && !showLoader && (
        <Box className={classes.modalFormContent}>
          {!showLoader && !showCopyBtn && (
            <FormControl>
              <FormLabel className={classes.formLabel}>{MACHINE_TYPE}</FormLabel>
              <RadioGroup row defaultValue="apim" onChange={handleMachineTypeChange}>
                <FormControlLabel
                  value="apim"
                  control={<Radio color="primary" size="small" />}
                  label={machineTypes[0].displayName}
                />
                <FormControlLabel
                  value="EFS"
                  control={<Radio color="primary" size="small" />}
                  label={machineTypes[1].displayName}
                />
              </RadioGroup>
            </FormControl>
          )}
        </Box>
      )}
      {selectedRealmInfo?.envType === 'admin' && formValues?.type === 'apim' && !showLoader ? (
        <Box>
          <FormControl>
            <FormLabel className={classes.formLabel}>{MACHINE_CATEGORY}</FormLabel>
            <RadioGroup row defaultValue="Internal" onChange={handleChangeMachineCategory}>
              <FormControlLabel value="Internal" control={<Radio color="primary" size="small" />} label="Internal" />
              <FormControlLabel value="External" control={<Radio color="primary" size="small" />} label="External" />
            </RadioGroup>
          </FormControl>
        </Box>
      ) : null}
      <Loader showLoader={showLoader} isCenter />
    </LuiModal>
  );
};

export default AddMachineDialog;
